import { Injectable } from '@angular/core';
import { ViewerSearchParams, ViewerSearchResult } from '../models/viewer-search-control';
import { Util } from '../utils/utils.module';

@Injectable()
export class ViewerSearchService {

  private highlightElement(index: number, viewerDocument: Document) {
    Util.Transforms.scrollToAndSetAttributeByQuerySelector(`#search_text_match_${index}`, viewerDocument, 'style', 'color: black; background-color: orange;');
  }

  private getTextBasedOnParams(text: string, searchParams: ViewerSearchParams): string {
    return searchParams.matchCase ? text : text?.toLocaleLowerCase();
  }

  private haveSearchText(textNode: Node, searchParams: ViewerSearchParams): boolean {
    return this.getTextBasedOnParams(textNode?.textContent, searchParams)?.includes(this.getTextBasedOnParams(searchParams.searchText, searchParams));
  }

  private escapeSearchText(searchText: string): string {
    return searchText.replace(/[.*+?^=!:${}()|[\]\\]/g, '\\$&');
  }

  private getSearchPattern(searchParams: ViewerSearchParams): RegExp {
    let flags: string = 'g';
    let pattern: string = this.escapeSearchText(searchParams.searchText);
    if (!(searchParams.matchCase ?? false)) {
      flags += 'i';
    }
    if (searchParams.matchWholeWord) {
      pattern = `\\b${pattern}\\b`;
    }
    return new RegExp(pattern, flags);
  }

  private encodeHTML(content: string, ele: string): string {
    const element = document.createElement(ele);
    if (content) {
      element.innerText = content;
      return element.innerHTML.replace(/<br\s*\/?>/gi, ' ');
    }
    return '';
  }

  private isClosingTagRequired(nodeName: string, outerHtml: string): boolean {
    return new RegExp(`<\\/\\s*${nodeName}\\s*>$`, 'i').test(outerHtml);
  }

  private getHighlightedContentAndCount(content: string, counter: number, searchPattern: RegExp, parentNodeName: string): any {
    let match: RegExpExecArray = null;
    let startIndex: number = 0;
    const endIndex: number = content.length;
    let resultContent: string = '';
    while (!!(match = searchPattern.exec(content))) {
      counter += 1;
      if (match.index > startIndex) {
        resultContent += this.encodeHTML(content.substring(startIndex, match.index), parentNodeName);
      }
      resultContent += `<mark class="searchTextOccurance" id="search_text_match_${counter}">${this.encodeHTML(match[0], parentNodeName)}</mark>`;
      startIndex = match.index + match[0].length;
    }
    if (startIndex < endIndex) {
      resultContent += this.encodeHTML(content.substring(startIndex, endIndex), parentNodeName);
    }
    return { content: resultContent, counter: counter };
  }

  public highlightSearchText(viewerDocument: Document, searchParams: ViewerSearchParams): ViewerSearchResult {
    const result: ViewerSearchResult = {
      totalOccurances: 0,
      currentIndex: 0,
      currentIndexOfTotal: ''
    };
    if (!searchParams || !searchParams.searchText || !viewerDocument || !viewerDocument?.body) {
      return result;
    }

    let counter: number = 0;
    let htmlContent: string = '';
    const searchPattern: RegExp = this.getSearchPattern(searchParams);
    const stack: [any] = [viewerDocument.body as Node];
    while (stack.length > 0) {
      const currentNode = stack.pop();
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const nodeName = currentNode.nodeName?.toLowerCase();
        htmlContent += `<${nodeName}`;

        const attributes = currentNode.attributes;
        for (let i = 0; i < attributes.length; i++) {
          htmlContent += ` ${attributes[i].name}="${attributes[i].value}"`;
        }
        if (this.isClosingTagRequired(nodeName, currentNode.outerHTML)) {
          htmlContent += '>';
          stack.push(`</${nodeName}>`);
          for (let i = currentNode.childNodes.length - 1; i >= 0; i--) {
            stack.push(currentNode.childNodes[i]);
          }
        } else {
          htmlContent += ' />';
        }
      } else if (currentNode.nodeType === Node.TEXT_NODE) {
        const parentNodeName = currentNode.parentNode.tagName?.toLowerCase();
        let content: string = currentNode.textContent || '';
        // check if curentNode gets rendered in UI. This is to eliminate text nodes of style tag and other hidden text nodes
        if (currentNode.parentElement?.checkVisibility() && this.haveSearchText(currentNode, searchParams)) {
          const result = this.getHighlightedContentAndCount(content, counter, searchPattern, parentNodeName);
          content = result.content;
          counter = result.counter;
        } else {
          content = this.encodeHTML(content, parentNodeName);
        }
        htmlContent += content;
      } else if (typeof currentNode === 'string') {
        htmlContent += currentNode;
      }
    }
    viewerDocument.body.innerHTML = htmlContent;

    result.totalOccurances = counter < 0 ? 0 : counter;
    if (result.totalOccurances > 0) {
      result.currentIndex = 1;
      this.highlightElement(result.currentIndex, viewerDocument);
    }
    result.currentIndexOfTotal = `${result.currentIndex}/${result.totalOccurances}`;
    return result;
  }

  public handleTraversal(result: ViewerSearchResult, up: boolean, viewerDocument: Document): void {
    if (!result || result.totalOccurances <= 0 || !viewerDocument) {
      return;
    }
    const previousElementId = `#search_text_match_${result.currentIndex}`;
    result.currentIndex = result.currentIndex + (up ? -1 : 1);
    if (result.currentIndex < 1) {
      result.currentIndex = result.totalOccurances;
    } else if (result.currentIndex > result.totalOccurances) {
      result.currentIndex = 1;
    }
    //1. Remove highlight for last visited occurance.
    Util.Transforms.removeAttributeByQuerySelector(previousElementId, viewerDocument, 'style');
    //2. Go to next search occurance
    this.highlightElement(result.currentIndex, viewerDocument);
    //3. Add or update counter info
    result.currentIndexOfTotal = `${result.currentIndex}/${result.totalOccurances}`;
  }

}

